import { useState } from "react";
import type { RunBackendState } from "@/api/types";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Props {
  backends: RunBackendState[];
}

export function RunBackendsPanel({ backends }: Props) {
  if (backends.length === 0) return null;

  return (
    <Card className="p-4 space-y-2">
      <h3 className="font-medium">Backends</h3>
      <ul className="space-y-1.5">
        {backends.map((b) => (
          <BackendRow key={b.backend_id} state={b} />
        ))}
      </ul>
    </Card>
  );
}

function BackendRow({ state }: { state: RunBackendState }) {
  const [open, setOpen] = useState(false);
  const failed = state.error !== null;
  // A backend "succeeded" ONLY if state.error is null AND its push_result
  // reports no internal error either. push_result.error captures partial
  // failures (some signals rejected) which the engine intentionally does
  // not cascade to state.error (spec §5 preserves the alert burst on
  // partial failure). Treating those as green would misrepresent the run.
  const pushErr = state.push_result?.error ?? null;
  const partial = !failed && state.push_result !== null && pushErr !== null;
  const ok = !failed && !partial && state.push_result !== null;
  const iconClass = ok
    ? "text-green-600 dark:text-green-400"
    : failed
      ? "text-red-600 dark:text-red-400"
      : partial
        ? "text-amber-600 dark:text-amber-400"
        : "text-muted-foreground";
  const icon = ok ? "✓" : failed ? "✗" : partial ? "!" : "⏳";
  const pushed = state.push_result?.pushed ?? {};
  const errorCode = failed ? state.error!.code : partial ? pushErr!.code : null;
  const errorMessage = failed ? state.error!.message : partial ? pushErr!.message : null;
  const showError = failed || partial;
  const errorTone = failed
    ? "text-red-600 dark:text-red-400"
    : "text-amber-600 dark:text-amber-400";
  return (
    <li className="border border-border rounded-md px-3 py-2">
      <div className="flex items-center gap-2 text-sm">
        <span className={cn("font-mono", iconClass)}>{icon}</span>
        <span className="font-medium">{state.backend_kind}</span>
        <span className="text-xs text-muted-foreground font-mono">{state.backend_id}</span>
        <span className="ml-auto text-xs font-mono text-muted-foreground">
          {ok
            ? Object.entries(pushed)
                .map(([k, v]) => `${k} ${v}`)
                .join(" · ")
            : showError && (
                <button
                  type="button"
                  onClick={() => setOpen((v) => !v)}
                  className={cn("underline underline-offset-2", errorTone)}
                  aria-expanded={open}
                  aria-controls={`error-detail-${state.backend_id}`}
                >
                  {errorCode}
                </button>
              )}
        </span>
      </div>
      {open && showError && (
        <div
          id={`error-detail-${state.backend_id}`}
          className="mt-1 text-xs font-mono text-muted-foreground"
        >
          <div>message: {errorMessage}</div>
        </div>
      )}
    </li>
  );
}
